import { useReadContract } from 'wagmi'
import poolAbi from '@/lib/abis/HorizonLendingPool.json'
import { ADDRESSES, IS_LIVE, ZERO_ADDR } from '@/lib/contracts'
import { POLL_FAST } from '@/lib/utils'

const Q96 = 2n ** 96n

const POOL_SLOT0_ABI = [{
  type: 'function', name: 'slot0', stateMutability: 'view', inputs: [],
  outputs: [
    { type: 'uint160', name: 'sqrtPriceX96' }, { type: 'int24', name: 'tick' },
    { type: 'uint16' }, { type: 'uint16' }, { type: 'uint16' }, { type: 'uint8' }, { type: 'bool' },
  ],
}]

/// HORIZON's price signal is the HORIZON/WETH Uniswap V3 pool (entry 187: the main pair moved from
/// USDx to WETH), composed with the lending pool's own Chainlink-backed WETH price: HORIZON/USD =
/// (WETH per HORIZON off slot0) x (USD per WETH off priceOf). Two reads instead of the USDx era's
/// one; the WETH pair is the deeper, more composable home for HORIZON's liquidity.
///
/// **One read, against the pool itself.** This used to take two dependent reads, resolving the
/// pool address out of `HorizonFarm`'s farm-0 struct first. That made the entire app's USD pricing
/// - vault TVL, APRs, portfolio values - a downstream dependency of the LP farm, which is being
/// retired now that HORIZON/USDx is a ALM vault like any other. The pool outlives every contract
/// that holds a position in it, so `ADDRESSES.HORIZON_WETH_POOL` points at it directly.
///
/// Token order is derived rather than read: Uniswap V3 always orders a pool's tokens by ascending
/// address, so comparing the two addresses this app already knows settles which side HORIZON is on
/// without spending a call.
export function useHorizonPriceUsd() {
  const poolAddr = ADDRESSES.HORIZON_WETH_POOL || ZERO_ADDR
  const horizonAddr = (ADDRESSES.HORIZON_TOKEN || '').toLowerCase()
  const wethAddr = (ADDRESSES.WETH || '').toLowerCase()
  const horizonIsToken0 = horizonAddr < wethAddr

  const { data: slot0 } = useReadContract({
    address: poolAddr, abi: POOL_SLOT0_ABI, functionName: 'slot0',
    query: { enabled: IS_LIVE && poolAddr !== ZERO_ADDR, refetchInterval: POLL_FAST },
  })
  const { data: ethUsdWei } = useReadContract({
    address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'priceOf', args: [ADDRESSES.WETH],
    query: { enabled: IS_LIVE && poolAddr !== ZERO_ADDR, refetchInterval: POLL_FAST },
  })

  if (!slot0 || !ethUsdWei) return { rate: 0, found: false, toUsd: () => 0 }
  const { rate, found } = computeHorizonPriceUsd(slot0[0], horizonIsToken0, Number(ethUsdWei) / 1e18)
  // `toUsd(amount)` folds the repeated `found ? amount * rate : 0` guard the callers hand-write, so a
  // consumer converts a HORIZON amount to USD without re-deriving the price gate each time.
  return { rate, found, toUsd: (amount) => (found && amount ? amount * rate : 0) }
}

/// Pure price math, split out from the hook above so it's testable without a wagmi
/// provider. token1/token0 (or its inverse) gives quote-asset per HORIZON; `quoteUsd`
/// converts that to USD - 1 for a stablecoin quote (the USDx era), the live ETH price
/// for the WETH pair (entry 187). Both sides are 18-decimal, so zero decimals math.
export function computeHorizonPriceUsd(sqrtPriceX96Raw, horizonIsToken0, quoteUsd = 1) {
  const sqrtPriceX96 = BigInt(sqrtPriceX96Raw)
  const priceX96 = (sqrtPriceX96 * sqrtPriceX96) / Q96 // token1 per token0, scaled by 2^96
  if (priceX96 === 0n) return { rate: 0, found: false }

  // Scale to a fixed-point BigInt before converting to Number - priceX96 is scaled by
  // 2^96, and Number() on a raw BigInt that large silently loses most of its precision.
  const SCALE = 10n ** 18n
  const rateWei = horizonIsToken0
    ? (priceX96 * SCALE) / Q96   // USDx per HORIZON = token1/token0
    : (Q96 * SCALE) / priceX96   // HORIZON is token1 -> invert
  const rate = (Number(rateWei) / 1e18) * quoteUsd

  return { rate, found: quoteUsd > 0 }
}
